import React, { useRef, useEffect } from 'react';
import { usePlayer } from '../context/PlayerContext';
import { FaPlay, FaPause, FaStepForward, FaStepBackward, FaRandom, FaRedoAlt } from 'react-icons/fa';

const Player = () => {
  const { 
    currentSong, 
    isPlaying, 
    togglePlay, 
    playNextSong, 
    playPreviousSong, 
    playlist,
    shuffle,
    toggleShuffle,
    repeat,
    toggleRepeat,
    progress,
    duration,
    sound
  } = usePlayer();
  
  // Référence pour la barre de progression
  const progressBarRef = useRef(null);

  // Format time in MM:SS
  const formatTime = (time) => {
    if (isNaN(time) || time === 0) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Calcul du pourcentage de progression pour la barre
  const progressPercent = duration > 0 ? (progress / duration) * 100 : 0;

  // Handle seeking
  const handleSeek = (e) => {
    if (!currentSong || !duration || !sound) return;
    
    const progressBar = progressBarRef.current;
    const position = (e.clientX - progressBar.getBoundingClientRect().left) / progressBar.offsetWidth;
    const seekTime = position * duration;
    
    sound.seek(seekTime);
  };

  // Create audio visualizer bars (simplified)
  const visualizerBars = Array.from({ length: 20 }, (_, i) => {
    const height = isPlaying ? Math.floor(Math.random() * 30) + 5 : 5;
    return (
      <div 
        key={i} 
        className="visualizer-bar" 
        style={{ height: `${height}px` }}
      />
    );
  });

  return (
    <div className="player">
      <div className="player-controls">
        <div className="player-info">
          <div>
            <div className="song-title">{currentSong?.title || 'No song selected'}</div>
            <div className="song-artist">{currentSong?.artist || ''}</div>
          </div>
          
          <div className="visualizer">
            {isPlaying && visualizerBars}
          </div>
        </div>
        
        <div className="player-buttons">
          <button 
            className={`player-btn ${shuffle ? 'active' : ''}`} 
            onClick={toggleShuffle}
            aria-label="Shuffle"
          >
            <FaRandom />
          </button>
          
          <button 
            className="player-btn" 
            onClick={playPreviousSong}
            disabled={!playlist || playlist.length <= 1}
            aria-label="Previous song"
          >
            <FaStepBackward />
          </button>
          
          <button 
            className="player-btn play-btn" 
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <FaPause /> : <FaPlay />}
          </button>
          
          <button 
            className="player-btn" 
            onClick={playNextSong}
            disabled={!playlist || playlist.length <= 1}
            aria-label="Next song"
          >
            <FaStepForward />
          </button>
          
          <button 
            className={`player-btn ${repeat !== 'off' ? 'active' : ''}`} 
            onClick={toggleRepeat}
            aria-label="Repeat"
          >
            <FaRedoAlt />
            {repeat === 'one' && <span className="repeat-one">1</span>}
          </button>
        </div>
        
        <div className="progress-container" ref={progressBarRef} onClick={handleSeek}>
          <div className="time">{formatTime(progress)}</div>
          <div className="progress-bar">
            <div 
              className="progress" 
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
          <div className="time">{formatTime(duration)}</div>
        </div>
      </div>
    </div>
  );
};

export default Player;
